<section class="life-timeline-page">
  <h1 class="life-timeline-page__title">{{ text().title }}</h1>

  <div class="life-timeline-page__controls">
    <ng-select
      class="life-timeline-page__chart"
      [items]="chartOptions()"
      bindValue="value"
      bindLabel="label"
      [placeholder]="text().chartPlaceholder"
      [ngModel]="chartId()"
      (ngModelChange)="onChartChosen($event)"
      data-test-id="life-timeline-chart-picker"
    ></ng-select>

    <!-- Weergave: tijdlijn of kalender (beslispunt 10). -->
    <div class="life-timeline-page__views" role="tablist">
      <button type="button" class="life-timeline-page__view"
              [class.is-active]="view() === 'timeline'"
              (click)="onViewChange('timeline')"
              data-test-id="life-timeline-view-timeline">
        <i class="fas fa-timeline" aria-hidden="true"></i> {{ text().viewTimeline }}
      </button>
      <button type="button" class="life-timeline-page__view"
              [class.is-active]="view() === 'calendar'"
              (click)="onViewChange('calendar')"
              data-test-id="life-timeline-view-calendar">
        <i class="fas fa-calendar-days" aria-hidden="true"></i> {{ text().viewCalendar }}
      </button>
    </div>

    <!-- Markdown-export van de momenten van deze chart (uitleg in de exportdialoog). -->
    @if (chartId() && momentMarkers().length > 0) {
      <button type="button" class="life-timeline-page__view life-timeline-page__export"
              [title]="text().exportTitle"
              (click)="openExport()"
              data-test-id="life-timeline-export">
        <i class="fas fa-file-export" aria-hidden="true"></i> {{ text().export }}
      </button>
    }

    <div class="life-timeline-page__layers">
      <label class="life-timeline-page__layer">
        <input type="checkbox" [ngModel]="showMoments()" (ngModelChange)="showMoments.set($event)"
               data-test-id="life-timeline-layer-moments" />
        {{ text().layerMoments }}
      </label>
      <label class="life-timeline-page__layer">
        <input type="checkbox" [ngModel]="showRanges()" (ngModelChange)="showRanges.set($event)"
               data-test-id="life-timeline-layer-ranges" />
        {{ text().layerRanges }}
      </label>
      <label class="life-timeline-page__layer">
        <input type="checkbox" [ngModel]="showCycles()" (ngModelChange)="showCycles.set($event)"
               data-test-id="life-timeline-layer-cycles" />
        {{ text().layerCycles }}
      </label>
      <!-- I Tjing-momenten (raadplegingen uit het dagboek), enkel met Classic I Ching-toegang. -->
      @if (ichingAccess.classicVisible()) {
        <label class="life-timeline-page__layer">
          <input type="checkbox" [ngModel]="showIChing()" (ngModelChange)="showIChing.set($event)"
                 data-test-id="life-timeline-layer-iching" />
          <i class="fa-regular fa-scroll-old" aria-hidden="true"></i> {{ text().layerIChing }}
        </label>
      }
      <!-- Categoriefilter (spec §2.7): daily default uit op de levenstijdlijn. -->
      @for (category of categories; track category) {
        <label class="life-timeline-page__layer">
          <input type="checkbox" [ngModel]="!hiddenCategories().has(category)"
                 (ngModelChange)="toggleCategoryFilter(category)"
                 [attr.data-test-id]="'life-timeline-category-' + category" />
          {{ categoryLabels()[category] }}
        </label>
      }
      <!-- Transit-strip (spec §2.5): eigen filterselectie, default de trage planeten. -->
      @if (view() === 'timeline') {
        <label class="life-timeline-page__layer life-timeline-page__layer--strip">
          <input type="checkbox" [ngModel]="showTransitStrip()"
                 (ngModelChange)="onStripToggle($event)"
                 data-test-id="life-timeline-strip-toggle" />
          {{ text().stripToggle }}
        </label>
        @if (showTransitStrip()) {
          @for (category of stripCategoryOrder; track category) {
            <label class="life-timeline-page__layer life-timeline-page__layer--strip-category">
              <input type="checkbox" [ngModel]="stripCategories().has(category)"
                     (ngModelChange)="toggleStripCategory(category)"
                     [attr.data-test-id]="'life-timeline-strip-' + category" />
              {{ stripCategoryLabels()[category] }}
            </label>
          }
        }
      }
    </div>
  </div>

  @if (error()) {
    <div class="life-timeline-page__error" role="alert">{{ error() }}</div>
  }

  @if (!chartId()) {
    <div class="life-timeline-page__empty" data-test-id="life-timeline-empty">{{ text().empty }}</div>
  } @else if (loading()) {
    <div class="life-timeline-page__empty">{{ text().loading }}</div>
  } @else if (standardBlocked()) {
    <div class="life-timeline-page__empty" data-test-id="life-timeline-standard-only">{{ text().standardOnly }}</div>
  } @else if (view() === 'calendar') {
    <!-- Kalenderweergave (spec §2.7): openen = mét Notities, toevoegen = daily. -->
    <div class="life-timeline-page__meta">{{ chartName() }}</div>
    <hd-moments-calendar
      [markers]="calendarMarkers()"
      (openRequested)="openChartWithNotes($event)"
      (consultationOpenRequested)="openConsultation($event)"
      (editRequested)="openMomentEdit($event)"
      (addRequested)="onCalendarAddRequested($event)"
    ></hd-moments-calendar>
  } @else {
    @if (birthUtc(); as birth) {
      <div class="life-timeline-page__meta">{{ chartName() }} · {{ birthText() }}</div>
      <hd-life-timeline
        [birthUtc]="birth"
        [markers]="visibleMarkers()"
        [showLabels]="showLabels()"
        (showLabelsChange)="onShowLabelsChange($event)"
        [colorMode]="colorMode()"
        (colorModeChange)="onColorModeChange($event)"
        [transitLanes]="stripLanes()"
        (viewportChanged)="onViewportChanged($event)"
        (openRequested)="openChart($event)"
        (consultationOpenRequested)="openConsultation($event)"
        (editRequested)="openMomentEdit($event)"
        (addRequested)="onAddRequested($event)"
      ></hd-life-timeline>
      @if (stripZoomTooCoarse()) {
        <div class="life-timeline-page__strip-hint" data-test-id="life-timeline-strip-hint">{{ text().stripZoomHint }}</div>
      }
    }
    @if (loadedWithoutBirth()) {
      <div class="life-timeline-page__empty" data-test-id="life-timeline-no-birth">{{ text().noBirth }}</div>
    }
  }
</section>

<!-- Momentdetails bewerken (potlood op popover/kalender, finetune 2026-09-21). -->
<hd-chart-edit-dialog
  [(target)]="editDialogTarget"
  [showMyChart]="false"
  (saved)="onMomentEditSaved($event)"
></hd-chart-edit-dialog>

<hd-moment-link-dialog
  [(target)]="momentDialogTarget"
  [sourcePreset]="sourcePreset()"
  [prefillDateUtc]="momentDialogPrefillUtc()"
  [defaultCategory]="momentDialogCategory()"
  (changed)="onMomentLinkChanged()"
  (chartCreated)="onMomentChartCreated($event)"
  (ichingRequested)="onIChingRequested($event)"
></hd-moment-link-dialog>

<!-- I Tjing-moment lezen (tijdlijn/kalender) of een nieuwe raadpleging starten (derde keuze). -->
<hd-iching-moment-dialog [(target)]="ichingDialogTarget"></hd-iching-moment-dialog>

<hd-moments-export-dialog [(target)]="exportTarget"></hd-moments-export-dialog>
